import {
  ClerkLoaded,
  ClerkLoading,
  ClerkFailed,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useUser
} from '@clerk/clerk-react'
import { Suspense, useEffect } from 'react'
import { Outlet, RouterProvider, ScrollRestoration, createBrowserRouter } from 'react-router-dom'
import Protect from './components/Protect.tsx'
import { ErrorBoundary } from './components/common/ErrorBoundary.tsx'
import DashboardPage from './components/pages/dashboard/index.tsx'
import ErrorPage from './components/pages/error.tsx'
import RootLayout from './components/pages/layout.tsx'
import MedicationRequestPage from './components/pages/medication-request/index.tsx'
import PrescribingHistoryPage from './components/pages/prescribing-history/index.tsx'
import useScreenshotAlert from './hooks/useScreenshotAlert.ts'
import InvestigationPage from './components/pages/investigation/index.tsx'
import posthog from 'posthog-js'
import useTrackTimeSpent from './hooks/useTrackTimeSpent.ts'
import LoadingSpinner from './components/ui/icons/loading-spinner.tsx'

const router = createBrowserRouter([
  {
    element: (
      <RootLayout>
        <ScrollRestoration />
        <Suspense fallback={<LoadingSpinner />}>
          <Protect
            roles={[
              'LEAD_PRESCRIBER',
              'PHARMACIST',
              'PRESCRIBER',
              'JUNIOR_PRESCRIBER',
              'VIEWER',
              'CS-VIEWER',
              'DIETITIAN',
              'TRIAL_PRESCRIBER',
              'CS_PHARMACY_MANAGER'
            ]}
            fallback='Missing presciberID or role configuration, please wait untill it is fixed'
          >
            <ErrorBoundary fallback='Error loading the page'>
              <Outlet />
            </ErrorBoundary>
          </Protect>
        </Suspense>
      </RootLayout>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <DashboardPage />
      },
      {
        path: '/history',
        element: <PrescribingHistoryPage />
      },
      {
        path: '/request/:id',
        element: <MedicationRequestPage />
      },
      {
        path: '/investigation/:id',
        element: <InvestigationPage />
      },
      {
        path: '*',
        element: <h1>Route Not found</h1>
      }
    ]
  }
])

function App() {
  useScreenshotAlert()
  useTrackTimeSpent()

  const { user } = useUser()
  const primaryEmail = user?.primaryEmailAddress?.emailAddress

  useEffect(() => {
    if (!user) return

    if (primaryEmail) {
      posthog?.identify(user.id, {
        email: primaryEmail,
        prescriberId: user.publicMetadata.prescriberId,
        role: user.publicMetadata.role
      })
    } else {
      console.warn('failed to load user data, reloading user...')
      user.reload()
    }
  }, [user, primaryEmail])

  return (
    <>
      <ClerkLoaded>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
        {primaryEmail && (
          <SignedIn>
            <RouterProvider router={router} />
          </SignedIn>
        )}
      </ClerkLoaded>
      <ClerkLoading>
        <div className='inset-0 fixed grid place-items-center'>
          <LoadingSpinner text='Loading Authentication' />
        </div>
      </ClerkLoading>
      <ClerkFailed>
        <h1>Failed to Load Authentication, try refreshing the page</h1>
      </ClerkFailed>
    </>
  )
}

export default App
